import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import api from "@/utils/api";
import { Package, CheckCircle2, AlertCircle, Edit2 } from "lucide-react";

const RMIssueLog = () => {
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editQty, setEditQty] = useState("");
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const fetchIssues = () => {
    setLoading(true);
    api.get("/rm-issues")
      .then((r) => setIssues(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchIssues(); }, []);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const saveQty = async (issueId) => {
    if (!editQty || parseFloat(editQty) < 0) {
      showToast("Enter a valid quantity", "error"); return;
    }
    setSaving(true);
    try {
      await api.put(`/rm-issues/${issueId}`, { quantity_issued_kg: parseFloat(editQty) });
      setIssues((prev) => prev.map((i) =>
        i.issue_id === issueId ? { ...i, quantity_issued_kg: parseFloat(editQty) } : i
      ));
      setEditingId(null);
      setEditQty("");
      showToast("Quantity updated successfully");
    } catch (err) {
      showToast(err.response?.data?.detail || "Failed to update", "error");
    } finally {
      setSaving(false);
    }
  };

  const pendingCount = issues.filter((i) => !i.quantity_issued_kg || i.quantity_issued_kg === 0).length;

  return (
    <div className="min-h-screen bg-slate-50 pb-4">
      <Header title="RM Issue Log" showBack backPath="/home" />

      {toast && (
        <div className={`fixed top-16 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 px-4 py-3 rounded-xl shadow-xl text-sm font-bold ${toast.type === "error" ? "bg-red-600 text-white" : "bg-[#15803D] text-white"}`}>
          {toast.type === "error" ? <AlertCircle size={16} /> : <CheckCircle2 size={16} />}
          {toast.msg}
        </div>
      )}

      <div className="max-w-2xl mx-auto px-4 py-4">
        {/* Summary */}
        <div className="bg-[#1A3560] rounded-2xl p-4 text-white mb-4 flex items-center gap-3">
          <Package size={28} className="text-white/70" />
          <div>
            <h2 className="font-extrabold text-lg">RM Issue Log</h2>
            <p className="text-white/60 text-xs">
              {pendingCount > 0
                ? `${pendingCount} issue(s) need quantity update`
                : "All issues have quantities recorded"}
            </p>
          </div>
        </div>

        <div className="flex items-center justify-between mb-3">
          <p className="text-xs font-bold uppercase tracking-widest text-slate-400">
            All Issues ({issues.length})
          </p>
          <button onClick={fetchIssues} className="text-xs text-[#1A3560] font-bold">Refresh</button>
        </div>

        {loading ? (
          <div className="text-center py-12 text-slate-400">Loading...</div>
        ) : issues.length === 0 ? (
          <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center text-slate-400">
            <Package size={32} className="mx-auto mb-2 opacity-30" />
            <p>No RM issues recorded yet</p>
            <p className="text-xs mt-1">Start a production job to create RM issue entries</p>
          </div>
        ) : (
          <div className="space-y-3">
            {issues.map((issue) => {
              const isPending = !issue.quantity_issued_kg || issue.quantity_issued_kg === 0;
              const isEditing = editingId === issue.issue_id;

              return (
                <div
                  key={issue.issue_id}
                  data-testid={`issue-${issue.issue_id}`}
                  className={`bg-white rounded-2xl border-2 p-4 transition-all ${
                    isPending ? "border-amber-200" : "border-slate-200"
                  }`}
                >
                  {/* Header */}
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <p className="font-extrabold text-[#1A3560] text-sm">{issue.full_batch_code}</p>
                      <p className="text-xs text-slate-400 mt-0.5">Job: {issue.job_id} • {issue.issue_date}</p>
                    </div>
                    {isPending ? (
                      <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full font-bold">Qty Pending</span>
                    ) : (
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-bold">
                        {issue.quantity_issued_kg} kg
                      </span>
                    )}
                  </div>

                  {/* Details grid */}
                  <div className="grid grid-cols-2 gap-2 text-xs text-slate-500 mb-3">
                    <span>Machine: <b className="text-slate-700">{issue.issued_to_machine}</b></span>
                    <span>Operator: <b className="text-slate-700">{issue.issued_to_operator}</b></span>
                    {issue.grade && <span>Grade: <b className="text-slate-700">{issue.grade}</b></span>}
                    {issue.thickness_mm && <span>Thickness: <b className="text-slate-700">{issue.thickness_mm}mm × {issue.width_mm}mm</b></span>}
                  </div>

                  {/* Edit or Update qty */}
                  {isEditing ? (
                    <div className="flex gap-2 items-center">
                      <input
                        data-testid={`qty-input-${issue.issue_id}`}
                        type="number"
                        step="0.1"
                        min="0"
                        value={editQty}
                        onChange={(e) => setEditQty(e.target.value)}
                        placeholder="Enter kg issued"
                        className="flex-1 h-11 px-3 rounded-xl border-2 border-[#1A3560] text-sm font-bold focus:outline-none"
                        autoFocus
                      />
                      <span className="text-sm text-slate-500 font-medium">kg</span>
                      <button
                        data-testid={`save-qty-${issue.issue_id}`}
                        onClick={() => saveQty(issue.issue_id)}
                        disabled={saving}
                        className="h-11 px-4 bg-[#15803D] text-white font-bold rounded-xl active:scale-[0.97] transition-all disabled:opacity-60"
                      >
                        {saving ? "..." : "Save"}
                      </button>
                      <button
                        onClick={() => { setEditingId(null); setEditQty(""); }}
                        className="h-11 px-3 bg-slate-100 text-slate-600 font-bold rounded-xl"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      data-testid={`edit-qty-${issue.issue_id}`}
                      onClick={() => { setEditingId(issue.issue_id); setEditQty(issue.quantity_issued_kg || ""); }}
                      className={`w-full h-11 rounded-xl text-sm font-bold flex items-center justify-center gap-2 transition-all active:scale-[0.97] ${
                        isPending
                          ? "bg-amber-50 text-amber-700 border-2 border-amber-200 hover:bg-amber-100"
                          : "bg-slate-50 text-slate-600 border border-slate-200 hover:bg-slate-100"
                      }`}
                    >
                      <Edit2 size={14} />
                      {isPending ? "Enter Quantity Issued / जारी मात्रा दर्ज करें" : `Update Qty (currently ${issue.quantity_issued_kg} kg)`}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default RMIssueLog;
